import axios from 'axios';
import {DateTime} from 'luxon';
import * as querystring from 'querystring';
import {SmsSenderServiceContract} from 'common/service/sender/SmsSenderServiceContract';
import {ExternalApiException} from 'common/exception/ExternalApiException';
import {Retry} from 'common/tool/Retry';
import {Logger} from 'common/tool/Logger';
import {Environment} from 'common/tool/Environment';
import {BadParameterException} from 'common/exception/BadParameterException';
import {Env} from 'common/tool/Env';
import {ValidatorUtil} from 'common/util/ValidatorUtil';
import {UrlUtil} from 'common/util/UrlUtil';

export class SmsSenderService implements SmsSenderServiceContract {
    public static readonly BASE_API_URL: string = 'https://api.smsmode.com/http/1.6/';
    public static readonly MESSAGE_MAX_LENGTH: number = 150;
    private readonly token: string;
    private readonly mode: Environment;

    public constructor(token: string, mode: Environment) {
        this.token = token;
        this.mode = mode;
    }

    public async send(senderName: string, message: string, rawPhones: string[], isCommercial: boolean = true, sendAt?: string): Promise<void> {
        let phones: string[] = this.checkPhoneFormat(rawPhones);
        if (phones.length === 0) {
            Logger.warning(new BadParameterException('no valid phone', {phones: rawPhones}));

            return Promise.resolve();
        }

        if (phones.length > 1) {
            if (message.length > SmsSenderService.MESSAGE_MAX_LENGTH * 2) {
                message = message.substring(0, SmsSenderService.MESSAGE_MAX_LENGTH * 2);
            } else if (message.length > SmsSenderService.MESSAGE_MAX_LENGTH && message.length <= SmsSenderService.MESSAGE_MAX_LENGTH * 1.33) {
                message = message.substring(0, SmsSenderService.MESSAGE_MAX_LENGTH);
            }
        }

        await Retry.request(async () => {
            const params: Record<string, any> = {
                accessToken: this.token,
                emetteur: senderName,
                message: UrlUtil.urlencode(message, 'ISO-8859-15'),
                numero: phones.map((phone) => phone.replace('+', '')).join(','),
                classe_msg: 2,
                nbr_msg: Math.ceil(message.length / SmsSenderService.MESSAGE_MAX_LENGTH)
            };

            if (isCommercial) {
                params.stop = 1;
            }

            if (sendAt !== undefined) {
                params.date_envoi = DateTime.fromISO(sendAt, { setZone: true }).toFormat('ddMMyyyy-HH:mm');
            }

            const target: DateTime = DateTime.fromISO(sendAt ?? (new Date()).toISOString(), { setZone: true }).setZone('Europe/Paris');
            if (isCommercial) { // do not send between 21h and 9h
                if (target.hour >= 21) {
                    params.date_envoi = target.plus({day: 1}).set({hour: 9}).toFormat('ddMMyyyy-HH:mm');
                } else if (target.hour < 9) {
                    params.date_envoi = target.set({hour: 9}).toFormat('ddMMyyyy-HH:mm');
                }
            } else { // do not send between 00h and 8h
                if (target.hour < 8) {
                    params.date_envoi = target.set({hour: 8}).toFormat('ddMMyyyy-HH:mm');
                }
            }

            if (this.mode === Env.TEST) {
                Logger.info('Simulation Mode: SMS has been sent', {params: params});
            } else {
                if (this.mode === Env.DEVELOPMENT) {
                    phones = ['+15145705214'];
                }

                const response: {data: string} = await axios.post(SmsSenderService.BASE_API_URL + 'sendSMS.do',
                    querystring.stringify(params, '&', '=', {encodeURIComponent: (p) => p}), {
                        timeout: 30000
                    });

                if (response.data.length > 0 && response.data.trim().substring(0, 1) !== '0') {
                    throw new ExternalApiException('Error: ' + response.data, params);
                }
            }
        }, 0, Logger.error.bind(Logger));
    }

    private checkPhoneFormat(phones: string[]): string[] {
        return phones.filter((phone) => ValidatorUtil.isIntMobilePhone(phone));
    }
}
